import { USER_ID_TOKEN } from '@/common/constants'
import useAccountStore from '@/account/accountStore'
import { ReactNode, useEffect, useState } from 'react'
import { toast } from 'sonner'
import * as Sentry from '@sentry/react'
import axios from 'axios'
import { app } from './apiClient'
import { fetchAuthSession } from 'aws-amplify/auth'
import { performManualSignOut } from '@/auth/performManualSignOut'
import { NetworkError, NotFoundError } from './error'
import { H1, Button } from '../ui'
import reportNetworkInfo from '../errorHandling/sentryUtils'
import { syncAccountIdHeader, shouldSendAccountIdHeader } from './axiosInterceptorPolicy.ts'

export type AxiosInterceptorProviderProps = {
    readonly children: ReactNode
}

// --- Module-level singleton for refresh promise ---
let activeRefresh: Promise<string | null> | null = null

const startRefresh = (setShowSessionExpiryScreen: (v: boolean) => void) => {
    if (!activeRefresh) {
        activeRefresh = (async () => {
            try {
                const session = await fetchAuthSession()
                const token = session.tokens?.idToken
                const idToken = token?.toString()
                if (!idToken) return null
                sessionStorage.setItem(USER_ID_TOKEN, idToken)
                return idToken
            } catch {
                try {
                    await performManualSignOut({ redirect: false, clearStorage: false })
                } catch {
                    // ignore sign-out failures
                }
                setShowSessionExpiryScreen(true)
                return null
            } finally {
                activeRefresh = null
            }
        })()
    }
    return activeRefresh
}

const RETRYABLE_METHODS = ['GET', 'HEAD', 'OPTIONS', 'PUT']
const NETWORK_TOAST_TIMEOUT_MS = 7000
let activeNetworkToastId: any = null

async function performNetworkRetries(config: any): Promise<any | null> {
    const method = (config?.method || '').toString().toUpperCase()
    if (!RETRYABLE_METHODS.includes(method)) return null

    try {
        const headers = syncAccountIdHeader(
            {
                ...(config?.headers || {}),
                'x-network-retry-attempt': '1',
            },
            config?.url,
            config?.method,
            useAccountStore.getState().activeAccountId,
        )

        // Diagnostics for retry attempt headers
        try {
            const shouldSend = shouldSendAccountIdHeader(config?.url, config?.method)
            const hasAccountHeader = Boolean(headers && (headers['x-account-id'] || headers['X-Account-Id']))
            if (shouldSend && !hasAccountHeader) {
                const activeAccountId = useAccountStore.getState().activeAccountId
                const msg = `Retry request missing x-account-id: ${String(config?.method)} ${String(config?.url)}`
                Sentry.captureException(new Error(msg), {
                    level: 'warning',
                    tags: { source: 'api-retry' },
                    extra: { activeAccountId, url: String(config?.url), method: String(config?.method) },
                })
            }
        } catch {
            // ignore
        }

        const retryConfig = {
            ...config,
            headers,
        }

        const retryResponse = await axios.request({
            baseURL: app.defaults?.baseURL,
            url: retryConfig.url,
            method: retryConfig.method,
            data: retryConfig.data,
            params: retryConfig.params,
            headers: retryConfig.headers,
            timeout: retryConfig.timeout ?? app.defaults?.timeout,
        })

        return retryResponse
    } catch (retryErr: any) {
        if (retryErr && retryErr.response) {
            return Promise.reject(retryErr)
        }
        // otherwise continue to next attempt
    }

    return null
}

function showNetworkToast(error: any) {
    if (activeNetworkToastId) return
    try {
        const networkMessage =
            error?.code === 'ECONNABORTED'
                ? 'The request timed out. Please try again.'
                : 'Network error — please check your internet connection and try again.'

        const id = toast.error(networkMessage)
        activeNetworkToastId = id
        setTimeout(() => {
            activeNetworkToastId = null
        }, NETWORK_TOAST_TIMEOUT_MS)
    } catch {
        // swallow toast errors
    }
}

export function AxiosInterceptorProvider({ children }: AxiosInterceptorProviderProps) {
    const [showSessionExpiryScreen, setShowSessionExpiryScreen] = useState(false)

    useEffect(() => {
        const requestInterceptorId = app.interceptors.request.use(
            async (config) => {
                config.headers = config.headers || {}
                const token = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(USER_ID_TOKEN) : null
                if (token) {
                    config.headers['Authorization'] = `Bearer ${token}`
                }
                syncAccountIdHeader(
                    config.headers,
                    config.url,
                    config.method,
                    useAccountStore.getState().activeAccountId,
                )

                // Diagnostics: record requests missing x-account-id — only when policy expects it
                try {
                    const shouldSend = shouldSendAccountIdHeader(config.url, config.method)
                    if (shouldSend) {
                        const hasAccountHeader = Boolean(
                            config.headers && (config.headers['x-account-id'] || config.headers['X-Account-Id']),
                        )
                        if (!hasAccountHeader) {
                            const activeAccountId = useAccountStore.getState().activeAccountId
                            const msg = `Outgoing request missing x-account-id: ${String(config.method)} ${String(
                                config.url,
                            )}`
                            Sentry.captureException(new Error(msg), {
                                level: 'warning',
                                tags: { source: 'api', url: String(config.url), method: String(config.method) },
                                extra: { activeAccountId, tokenPresent: !!token },
                            })
                        }
                    }
                } catch {
                    // swallow diagnostics failures
                }

                return config
            },
            (error) => Promise.reject(error),
        )

        const responseInterceptorId = app.interceptors.response.use(
            (response) => response,
            async (error) => {
                let currentError: any = error
                let response = error?.response
                const config = error?.config

                if (!response) {
                    try {
                        const retryResult = await performNetworkRetries(config)
                        if (retryResult) return retryResult
                    } catch (retryErr: any) {
                        if (retryErr && retryErr.response) {
                            // Use the retry error/response and continue through normal handling
                            currentError = retryErr
                            response = retryErr.response
                        } else {
                            // Non-response network error during retry
                            showNetworkToast(currentError)
                            reportNetworkInfo(currentError)
                            return Promise.reject(new NetworkError())
                        }
                    }

                    if (!response) {
                        // Retries exhausted or method not retryable
                        showNetworkToast(currentError)
                        reportNetworkInfo(currentError)

                        return Promise.reject(new NetworkError())
                    }
                }

                if (response.status === 404) {
                    return Promise.reject(new NotFoundError(response.data))
                }

                if (response.status !== 401) {
                    try {
                        Sentry.captureException(currentError, {
                            level: 'error',
                            tags: {
                                source: 'api',
                                status: String(response.status),
                            },
                            extra: {
                                url: response.config?.url,
                                method: response.config?.method,
                                status: response.status,
                                responseData: response.data,
                                requestId: currentError.requestId,
                                traceId: currentError.traceId,
                            },
                        })
                    } catch {
                        // swallow Sentry errors
                    }
                    return Promise.reject(currentError)
                }

                if ((config as any)?._retry) {
                    setShowSessionExpiryScreen(true)
                    return Promise.reject(currentError)
                }

                const newToken = await startRefresh(setShowSessionExpiryScreen)
                if (!newToken) {
                    setShowSessionExpiryScreen(true)
                    return Promise.reject(error)
                }

                const configWithRetry = {
                    ...config,
                    headers: {
                        ...(config.headers || {}),
                        Authorization: `Bearer ${newToken}`,
                    },
                    _retry: true,
                } as typeof config & { _retry: boolean }

                return app.request(configWithRetry)
            },
        )

        return () => {
            app.interceptors.request.eject(requestInterceptorId)
            app.interceptors.response.eject(responseInterceptorId)
        }
    }, [])

    const handleSignIn = async () => {
        try {
            await performManualSignOut({ redirect: false, clearStorage: false })
        } catch {
            // ignore sign-out failures
        }
        try {
            useAccountStore.getState().clearAll()
        } catch {
            // swallow store errors
        }

        setShowSessionExpiryScreen(false)
    }

    if (showSessionExpiryScreen) {
        return (
            <div className="grid h-screen place-content-center p-8 gap-8">
                <H1 className="text-center mb-0">Signed Out</H1>
                <p className="text-center text-balance">
                    For security, you have been signed out. Please sign in again.
                </p>
                <Button variant="secondary" size="lg" onClick={handleSignIn}>
                    Sign In
                </Button>
            </div>
        )
    }

    return <>{children}</>
}
